import { useState } from "react";
import { LoaderFunction, useLoaderData, useNavigate } from "react-router-dom";

import { FilterAndSortRetailProducts } from "@/pages/root/shop/FilterAndSortRetailProducts";
import { get, pget } from "api";
import { AffirmBanner } from "atoms/affirm/affirmBanner/AffirmBanner";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import useDunningMembership from "hooks/useDunningMemberships";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import AdonisCTAVariation2 from "modules/adonis/AdonisCTAVariation2/AdonisCTAVariation2";
import { Analytics, AnalyticsEvents } from "modules/analytics";
import {
  filterItemsByProducts,
  getProductIds,
  isBestSeller,
  isFeatured,
  shouldShowAdonisCTA,
  shouldShowAffirmBanner,
} from "modules/retailProducts";
import { AffirmModal } from "molecules/affirm-modal/AffirmModal";
import { ProductCardSmall } from "molecules/cards/product-card-small/ProductCardSmall";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { getRetailProductsWithDiscountInformation } from "services/ListingHelperService";
import { waitObj } from "toolbox/Promise";

import { ShopListHeader } from "./ShopListHeader/ShopListHeader";

const getMembershipDiscounts = pget("/memberships/discount-prices");

const loader = async () => {
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  const products = queryClient.fetchQuery(
    ["/retailproducts/{platformId}/product-listings"],
    async () => {
      const platformData = await platform;
      return await get("/retailproducts/{platformId}/product-listings", {
        platformId: platformData.id,
      });
    }
  );

  const productTypes = queryClient.fetchQuery(
    ["/retailproducts/product-types"],
    async () => {
      return await get("/retailproducts/product-types", {
        languageCode: 0,
      });
    }
  );

  const brands = queryClient.fetchQuery(
    ["/retailproducts/{platformId}/default/brands"],
    async () => {
      const platformData = await platform;
      return await get("/retailproducts/{platformId}/default/brands", {
        platformId: platformData.id,
      });
    }
  );

  const shopData = await get("/shop");
  const theme = queryClient.fetchQuery(["/platform/theme"], () =>
    get("/platform/theme")
  );

  const shopDiscounts = queryClient.fetchQuery(
    ["/shop/discounts"],
    () => get("/shop/discounts"),
    { staleTime: 1000 * 10 }
  );

  const customShopCategories = queryClient.fetchQuery(
    ["/shop/custom-categories/listings"],
    () => get("/shop/custom-categories/listings"),
    { staleTime: 1000 * 10 }
  );

  const user = queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  const membershipDiscounts = queryClient.fetchQuery({
    ...getMembershipDiscounts.keyed(),
  });

  return waitObj({
    platform,
    theme,
    shopData,
    products,
    productTypes,
    brands,
    shopDiscounts,
    customShopCategories,
    user,
    membershipDiscounts,
  });
};

type LoaderData = Awaited<ReturnType<typeof loader>>;
export const shopProductsListLoader: LoaderFunction = loader;

const ShopProductsListPage = () => {
  const {
    platform,
    theme,
    shopData,
    products,
    user,
    productTypes,
    brands,
    shopDiscounts,
    customShopCategories,
    membershipDiscounts,
  } = useLoaderData() as LoaderData;

  const { openChat, isEnabled: isAdonisEnabled } = useAdonisChat();
  const navigate = useNavigate();
  const [showAffirmModal, setShowAffirmModal] = useState(false);

  const { dunningMembership } = useDunningMembership();
  const adonisShopCTAText = useFeatureFlag("adonisShopCTAText");

  const productConcernIds = getProductIds(products, "concernIds");
  const productAreaIds = getProductIds(products, "areaIds");

  const filteredConcerns = filterItemsByProducts(
    shopData.concerns ?? [],
    productConcernIds
  );
  const filteredAreas = filterItemsByProducts(
    shopData.areas ?? [],
    productAreaIds
  );

  const handleAdonisCTAClick = () => {
    openChat();
    Analytics.track(AnalyticsEvents.ADONIS_VIEWED, {});
  };

  const headerImage = theme.resources?.find(
    (resource) => resource.key === "retail-products-header"
  )?.url;

  const productsWithDiscounts = getRetailProductsWithDiscountInformation(
    products,
    customShopCategories,
    shopDiscounts?.listingDiscounts
  );

  return (
    <div>
      <div className="flex min-h-screen flex-col bg-two">
        <TopNav
          startIconSVG={ChevronLeft}
          onStartIconClick={() => {
            navigate("/shop/products");
          }}
        >
          <h1 className="text-title">Browse</h1>
        </TopNav>
        {platform.platformSettings?.affirmEnabled && (
          <AffirmModal
            platformLogo={platform.logoImageUrl ?? ""}
            open={showAffirmModal}
            onClose={() => setShowAffirmModal(false)}
            hideRewardText={!platform?.platformSettings?.rewardsEnabled}
          />
        )}

        <ShopListHeader
          title="Products"
          subtitle="Shop our collection of bespoke products"
          bgGradient
          backgroundImageUrl={headerImage}
        />

        <FilterAndSortRetailProducts
          areas={filteredAreas}
          concerns={filteredConcerns}
          brands={brands}
          productTypes={productTypes}
          retailProducts={productsWithDiscounts}
        >
          {(product, index) => {
            const showAdonisCTA = shouldShowAdonisCTA(index, isAdonisEnabled);
            const showAffirmBanner = shouldShowAffirmBanner(
              index,
              dunningMembership,
              platform
            );

            const membershipDiscount = membershipDiscounts.find(
              (md) => md.packageId === product.variants[0]?.id
            );

            return (
              <>
                <ProductCardSmall
                  key={product.id}
                  itemId={product.id}
                  title={product.name.text}
                  description={product.description.text}
                  imageUrl={product.imageUris}
                  featuredTag={isFeatured(product)}
                  tag={isBestSeller(product, user)}
                  type={
                    product.type === 1 ? "Product" : "ProductFromExternalSource"
                  }
                  price={product.oneTimePurchasePriceInCents}
                  memberPrice={membershipDiscount?.discountedAmountInCents}
                  onClick={() => navigate(`/pdp/product/${product.id}`)}
                  guestMode={user?.isGuest}
                  discount={product.discountText}
                  customColor={product.customColor}
                />
                {showAdonisCTA && (
                  <div className="col-span-2">
                    <AdonisCTAVariation2
                      onClick={handleAdonisCTAClick}
                      ctaText={adonisShopCTAText}
                    />
                  </div>
                )}
                {showAffirmBanner && (
                  <div className="col-span-2">
                    <AffirmBanner
                      onClick={() => setShowAffirmModal(true)}
                      hasColorGradient
                      hasBorderRadius
                      hasShadow
                      hideRewardText={
                        !platform?.platformSettings?.rewardsEnabled
                      }
                    />
                  </div>
                )}
              </>
            );
          }}
        </FilterAndSortRetailProducts>

        <div className="mt-auto">
          <RepeatMDFooter />
        </div>
      </div>
    </div>
  );
};

export default ShopProductsListPage;
